// ═══════════════════════════════════════════════════════════════
// TemplateBatchModal.tsx – Sablon batch mód (csak superadmin)
// A „Közzétételi lista 2026 – Infotv. 1. melléklet (új lista)” betöltése több szervezetre:
// szervezetenként látszik, be van-e már töltve; a kijelöltekre Kiegészítés / Átállás / Teljes újraírás
// futtatható, szervezetenként egymás után, folyamatjelzővel. A változók: a szervezetnél
// elmentett értékek, a többi a szervezet alapadataiból (a szerver oldja fel).
// ═══════════════════════════════════════════════════════════════
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ApiClient, type TemplateStatusRow } from '../../api/client';
import { X, RefreshCw, Search, Play, Eye, CheckCircle2, AlertTriangle, Square, Layers } from 'lucide-react';

type Mode = 'MERGE' | 'MIGRATE' | 'REPLACE' | 'TITLES' | 'SNIPPETS';
type Filter = 'missing' | 'all' | 'full';
type RowResult = { state: 'running' | 'ok' | 'error' | 'preview'; text: string };

const STATUS: Record<TemplateStatusRow['status'], { label: string; cls: string }> = {
  none: { label: 'Nincs lista', cls: 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700' },
  legacy: { label: 'Régi lista', cls: 'bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800' },
  partial: { label: '2026 – részleges', cls: 'bg-sky-50 text-sky-800 border-sky-200 dark:bg-sky-950/40 dark:text-sky-300 dark:border-sky-800' },
  full: { label: '2026 – teljes', cls: 'bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800' },
  other: { label: 'Egyéb tartalom', cls: 'bg-violet-50 text-violet-800 border-violet-200 dark:bg-violet-950/40 dark:text-violet-300 dark:border-violet-800' },
};

const MODES: { id: Mode; label: string; desc: string; cls: string }[] = [
  { id: 'MERGE', label: 'Kiegészítés (Merge)', desc: 'Csak a hiányzó 2026-os pontok jönnek létre, a meglévők érintetlenek.', cls: 'bg-indigo-600' },
  { id: 'MIGRATE', label: 'Átállás a 2026-os listára', desc: 'A régi számozású pontok átszámozódnak (tartalom, mellékletek maradnak), a hiányzók létrejönnek.', cls: 'bg-indigo-600' },
  { id: 'TITLES', label: 'Címek frissítése a sablon szerint', desc: 'A betöltött főpontok címe, frissítése és megőrzése a (javított) listához igazodik; tartalom, mellékletek maradnak.', cls: 'bg-indigo-600' },
  { id: 'SNIPPETS', label: 'Tartalom-változók frissítése', desc: 'A sablonban tartalom-változóra hivatkozó pontok leírása újratöltődik a tartalom-változó mostani tartalmával (a leírás kézi módosítása felülíródik – előbb előnézet).', cls: 'bg-indigo-600' },
  { id: 'REPLACE', label: 'Teljes újraírás', desc: 'A sablon oldalain a meglévő bejegyzések inaktiválódnak, és a 2026-os lista újra létrejön.', cls: 'bg-rose-600' },
];

const fmtDate = (s: string | null) => (s ? new Date(s.replace(' ', 'T') + 'Z').toLocaleString('hu-HU', { dateStyle: 'short', timeStyle: 'short' }) : '–');
const norm = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

export const TemplateBatchModal: React.FC<{ isOpen: boolean; onClose: () => void; onDone?: () => void }> = ({ isOpen, onClose, onDone }) => {
  const [rows, setRows] = useState<TemplateStatusRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('missing');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [mode, setMode] = useState<Mode>('MERGE');
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number; current: string } | null>(null);
  const [results, setResults] = useState<Record<number, RowResult>>({});
  const [summary, setSummary] = useState<string | null>(null);
  const stopRef = useRef(false);

  const load = async () => {
    setLoading(true); setError(null);
    const r = await ApiClient.getTemplateStatus();
    if (r.ok && r.data) setRows(r.data);
    else setError(r.error || 'Az állapot nem kérhető le');
    setLoading(false);
  };
  useEffect(() => { if (isOpen) { load(); setResults({}); setSummary(null); setSelected(new Set()); } }, [isOpen]);

  const visible = useMemo(() => {
    const q = norm(search.trim());
    return rows.filter((r) => {
      if (filter === 'missing' && r.status === 'full') return false;
      if (filter === 'full' && r.status !== 'full') return false;
      return !q || norm(`${r.name} ${r.slug}`).includes(q);
    });
  }, [rows, filter, search]);
  const counts = useMemo(() => ({ all: rows.length, missing: rows.filter((r) => r.status !== 'full').length, full: rows.filter((r) => r.status === 'full').length }), [rows]);
  const allVisibleSelected = visible.length > 0 && visible.every((r) => selected.has(r.id));

  if (!isOpen) return null;

  const toggle = (id: number) => setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const toggleAll = () => setSelected((s) => {
    const n = new Set(s);
    if (allVisibleSelected) visible.forEach((r) => n.delete(r.id)); else visible.forEach((r) => n.add(r.id));
    return n;
  });

  const run = async (dryRun: boolean) => {
    const targets = rows.filter((r) => selected.has(r.id));
    if (!targets.length) return;
    stopRef.current = false;
    setRunning(true); setSummary(null);
    setResults((prev) => { const n = { ...prev }; targets.forEach((t) => { delete n[t.id]; }); return n; });
    let ok = 0; let failed = 0; let created = 0; let renumbered = 0;
    for (let i = 0; i < targets.length; i++) {
      if (stopRef.current) break;
      const t = targets[i];
      setProgress({ done: i, total: targets.length, current: t.name });
      setResults((p) => ({ ...p, [t.id]: { state: 'running', text: dryRun ? 'előnézet…' : 'fut…' } }));
      const res = await ApiClient.batchProvisionTemplates({ tenant_ids: [t.id], mode, ...(dryRun ? { dry_run: true } : {}) });
      const item = res.ok ? res.data?.results?.[0] ?? (res as any).results?.[0] : null;
      const errItem = res.ok ? (res.data?.errors?.[0] ?? (res as any).errors?.[0]) : null;
      if (!res.ok || errItem || !item) {
        failed++;
        setResults((p) => ({ ...p, [t.id]: { state: 'error', text: errItem?.error || res.error || 'Hiba' } }));
        continue;
      }
      ok++;
      if (mode === 'SNIPPETS') {
        const pts = (item.changes || []).map((c: any) => c.pont);
        const miss = (item.missingSnippets || []).length ? ` · ${(item.missingSnippets || []).length} hiányzó tartalom-változó` : '';
        const text = item.updated
          ? `${item.updated} pont leírása ${dryRun ? 'frissülne' : 'frissítve'}: ${pts.slice(0, 8).join(', ')}${pts.length > 8 ? '…' : ''}${miss}`
          : `Nincs eltérés${miss}`;
        if (!dryRun) renumbered += item.updated || 0;
        setResults((p) => ({ ...p, [t.id]: { state: dryRun ? 'preview' : 'ok', text } }));
      } else if (mode === 'TITLES') {
        const pts = (item.changes || []).map((c: any) => c.pont);
        const text = item.updated
          ? `${item.updated} pont ${dryRun ? 'frissülne' : 'frissítve'}: ${pts.slice(0, 8).join(', ')}${pts.length > 8 ? '…' : ''}`
          : 'Nincs eltérés a listától';
        if (!dryRun) renumbered += item.updated || 0;
        setResults((p) => ({ ...p, [t.id]: { state: dryRun ? 'preview' : 'ok', text } }));
      } else if (dryRun) {
        const text = `${item.renumbered || 0} átszámozás · ${(item.toCreate || []).length} új pont${(item.unmapped || []).length ? ` · ${(item.unmapped || []).length} megfeleltetés nélkül marad` : ''}`;
        setResults((p) => ({ ...p, [t.id]: { state: 'preview', text } }));
      } else {
        created += item.createdRecordsCount || 0; renumbered += item.renumbered || 0;
        const text = mode === 'MIGRATE'
          ? `${item.renumbered || 0} átszámozva, ${item.createdRecordsCount || 0} pont létrehozva`
          : `${item.createdRecordsCount || 0} pont létrehozva${item.createdPagesCount ? `, ${item.createdPagesCount} új oldal` : ''}`;
        setResults((p) => ({ ...p, [t.id]: { state: 'ok', text } }));
      }
    }
    setProgress(null);
    setRunning(false);
    const stopped = stopRef.current ? ' (megállítva)' : '';
    setSummary(dryRun
      ? `Előnézet kész${stopped}: ${ok} szervezet${failed ? `, ${failed} hiba` : ''}. Nem történt módosítás.`
      : mode === 'SNIPPETS'
        ? `Kész${stopped}: ${ok} szervezet${failed ? `, ${failed} hiba` : ''} · ${renumbered} pont leírása frissítve a tartalom-változókból.`
        : mode === 'TITLES'
        ? `Kész${stopped}: ${ok} szervezet${failed ? `, ${failed} hiba` : ''} · ${renumbered} pont címe / adata frissítve.`
        : `Kész${stopped}: ${ok} szervezet${failed ? `, ${failed} hiba` : ''} · ${created} pont létrehozva${renumbered ? `, ${renumbered} átszámozva` : ''}.`);
    if (!dryRun) { await load(); onDone?.(); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-xs overflow-y-auto" role="dialog" aria-modal="true" aria-labelledby="tb-title">
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl w-full max-w-6xl shadow-2xl my-auto max-h-[92vh] flex flex-col">
        <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-800 flex items-start justify-between gap-3">
          <div>
            <h2 id="tb-title" className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2"><Layers className="w-5 h-5 text-indigo-600" /> Sablon – batch mód</h2>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              Közzétételi lista 2026 – Infotv. 1. melléklet (új lista). A változók: a szervezetnél elmentett értékek, a többi a szervezet alapadataiból.
            </p>
          </div>
          <button type="button" onClick={onClose} disabled={running} aria-label="Bezárás" className="p-2 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer disabled:opacity-40"><X className="w-5 h-5" /></button>
        </div>

        <div className="px-5 py-3 border-b border-slate-200 dark:border-slate-800 flex flex-wrap items-center gap-2">
          <div className="flex items-center bg-slate-100 dark:bg-slate-800 p-1 rounded-xl border border-slate-200 dark:border-slate-700" role="group" aria-label="Szűrés">
            {([['missing', 'Ahol nincs betöltve', counts.missing], ['full', 'Betöltve', counts.full], ['all', 'Összes', counts.all]] as const).map(([id, label, n]) => (
              <button key={id} type="button" aria-pressed={filter === id} onClick={() => setFilter(id)}
                className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold cursor-pointer ${filter === id ? 'bg-white dark:bg-slate-900 text-indigo-600 dark:text-indigo-400 shadow-xs' : 'text-slate-500'}`}>
                {label} <span className="ml-1 text-[10px] opacity-70">{n}</span>
              </button>
            ))}
          </div>
          <div className="relative flex-1 min-w-[12rem]">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Keresés név vagy azonosító szerint…" aria-label="Szervezet keresése"
              className="w-full pl-8 pr-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-700 rounded-xl text-xs text-slate-900 dark:text-white" />
          </div>
          <button type="button" onClick={load} disabled={loading || running} title="Frissítés" className="p-2 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer disabled:opacity-40">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        <div className="flex-1 overflow-auto">
          {error && <p className="m-5 text-sm text-rose-600" role="alert">{error}</p>}
          <table className="w-full text-left text-xs">
            <thead className="sticky top-0 bg-slate-50 dark:bg-slate-950 text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200 dark:border-slate-800">
              <tr>
                <th className="py-2 px-3 w-8"><input type="checkbox" aria-label="Az összes látható kijelölése" checked={allVisibleSelected} onChange={toggleAll} disabled={running} /></th>
                <th className="py-2 px-3">Szervezet</th>
                <th className="py-2 px-3">Állapot</th>
                <th className="py-2 px-3">2026-os pontok</th>
                <th className="py-2 px-3 text-right">Régi pontok</th>
                <th className="py-2 px-3 text-right">Oldal / bejegyzés</th>
                <th className="py-2 px-3">Utolsó sablonművelet</th>
                <th className="py-2 px-3">Eredmény</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800/70">
              {visible.map((r) => {
                const st = STATUS[r.status];
                const pct = r.total ? Math.round((r.found / r.total) * 100) : 0;
                const res = results[r.id];
                return (
                  <tr key={r.id} className={`${selected.has(r.id) ? 'bg-indigo-50/50 dark:bg-indigo-950/20' : ''} hover:bg-slate-50 dark:hover:bg-slate-800/30`}>
                    <td className="py-2 px-3"><input type="checkbox" aria-label={`${r.name} kijelölése`} checked={selected.has(r.id)} onChange={() => toggle(r.id)} disabled={running} /></td>
                    <td className="py-2 px-3">
                      <div className="font-semibold text-slate-900 dark:text-white">{r.name}</div>
                      <div className="text-[10px] text-slate-400 font-mono">{r.slug}{r.is_demo ? ' · DEMO' : ''}{!r.active ? ' · inaktív' : ''}</div>
                    </td>
                    <td className="py-2 px-3"><span className={`px-2 py-0.5 rounded-full border text-[10px] font-semibold whitespace-nowrap ${st.cls}`}>{st.label}</span></td>
                    <td className="py-2 px-3 min-w-[9rem]">
                      <div className="flex items-center gap-2">
                        <div className="h-1.5 flex-1 rounded-full bg-slate-200 dark:bg-slate-800 overflow-hidden" aria-hidden="true"><div className={`h-full ${pct === 100 ? 'bg-emerald-500' : 'bg-indigo-500'}`} style={{ width: `${pct}%` }} /></div>
                        <span className="tabular-nums text-slate-600 dark:text-slate-300 whitespace-nowrap">{r.found}/{r.total}</span>
                      </div>
                    </td>
                    <td className="py-2 px-3 text-right tabular-nums">{r.legacy || '–'}</td>
                    <td className="py-2 px-3 text-right tabular-nums whitespace-nowrap">{r.pages} / {r.records}</td>
                    <td className="py-2 px-3 whitespace-nowrap text-slate-500">{fmtDate(r.last_template_at)}</td>
                    <td className="py-2 px-3 text-[11px]">
                      {res && (
                        <span className={`inline-flex items-center gap-1 ${res.state === 'ok' ? 'text-emerald-700 dark:text-emerald-400' : res.state === 'error' ? 'text-rose-600' : res.state === 'preview' ? 'text-sky-700 dark:text-sky-300' : 'text-slate-500'}`}>
                          {res.state === 'running' && <RefreshCw className="w-3 h-3 animate-spin" />}
                          {res.state === 'ok' && <CheckCircle2 className="w-3 h-3" />}
                          {res.state === 'error' && <AlertTriangle className="w-3 h-3" />}
                          {res.state === 'preview' && <Eye className="w-3 h-3" />}
                          {res.text}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {!loading && !visible.length && (
                <tr><td colSpan={8} className="py-10 text-center text-slate-500">{filter === 'missing' ? 'Minden szervezetnél be van töltve a 2026-os lista.' : 'Nincs találat.'}</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="px-5 py-3 border-t border-slate-200 dark:border-slate-800 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2" role="radiogroup" aria-label="Telepítési mód">
            {MODES.map((m) => (
              <button key={m.id} type="button" role="radio" aria-checked={mode === m.id} onClick={() => setMode(m.id)} disabled={running}
                className={`text-left p-2.5 rounded-xl border text-xs cursor-pointer disabled:opacity-50 ${mode === m.id ? (m.id === 'REPLACE' ? 'border-rose-500 bg-rose-50/60 dark:bg-rose-950/20' : 'border-indigo-500 bg-indigo-50/60 dark:bg-indigo-950/20') : 'border-slate-200 dark:border-slate-700'}`}>
                <span className={`font-bold ${m.id === 'REPLACE' ? 'text-rose-700 dark:text-rose-400' : 'text-slate-900 dark:text-white'}`}>{m.label}</span>
                <span className="block mt-0.5 text-[11px] text-slate-500 dark:text-slate-400">{m.desc}</span>
              </button>
            ))}
          </div>
          {progress && (
            <div className="space-y-1" role="status" aria-live="polite">
              <div className="h-2 rounded-full bg-slate-200 dark:bg-slate-800 overflow-hidden"><div className="h-full bg-indigo-600 transition-[width]" style={{ width: `${Math.round((progress.done / progress.total) * 100)}%` }} /></div>
              <div className="text-xs text-slate-600 dark:text-slate-300">{progress.done + 1}/{progress.total} – {progress.current}</div>
            </div>
          )}
          {summary && !progress && <p className="text-xs font-semibold text-slate-700 dark:text-slate-200" role="status">{summary}</p>}
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-xs text-slate-500">{selected.size} szervezet kijelölve</span>
            <div className="flex items-center gap-2">
              {running ? (
                <button type="button" onClick={() => { stopRef.current = true; }} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 cursor-pointer">
                  <Square className="w-3.5 h-3.5" /> Megállítás (az aktuális szervezet után)
                </button>
              ) : (
                <>
                  {(mode === 'MIGRATE' || mode === 'TITLES' || mode === 'SNIPPETS') && (
                    <button type="button" onClick={() => run(true)} disabled={!selected.size} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold border border-indigo-300 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 cursor-pointer disabled:opacity-40">
                      <Eye className="w-3.5 h-3.5" /> Előnézet
                    </button>
                  )}
                  <button type="button" onClick={() => run(false)} disabled={!selected.size}
                    className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold text-white cursor-pointer disabled:opacity-40 ${mode === 'REPLACE' ? 'bg-rose-600 hover:bg-rose-500' : 'bg-indigo-600 hover:bg-indigo-500'}`}>
                    <Play className="w-3.5 h-3.5" /> {MODES.find((m) => m.id === mode)!.label} – {selected.size} szervezet
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplateBatchModal;
